import { FullScreenLoader } from '@/components/molecules';
import { Toast } from '@/components/molecules/Toast';
import React from 'react';
import { Platform } from 'react-native';
import { AppointmentCanceledDialog } from './AppointmentCanceledDialog';
import { AppointmentConfirmedDialog } from './AppointmentConfirmedDialog';
import { AppointmentSlotDialog } from './AppointmentSlotDialog';
import { CancelAppointmentDialog } from './CancelAppointmentDialog';
import type { useBookAppointmentFlow } from '../hooks/useBookAppointmentFlow';

type Flow = ReturnType<typeof useBookAppointmentFlow>;

export interface AppointmentFlowDialogsProps {
  flow: Flow;
  /** Overrides slot submit CTA (e.g. listing uses "Reschedule"). */
  rescheduleSubmitLabel?: string;
}

function getSlotInitialSelection(flow: Flow): {
  initialDateId: string | null;
  initialTimeId?: string;
  bookedSlotId?: string;
} {
  if (flow.slotMode === 'reschedule' && flow.appointment) {
    const bookedSlotId =
      flow.appointment.appointmentId?.trim() ||
      flow.appointment.timeId ||
      undefined;
    return {
      initialDateId: flow.appointment.dateId,
      // Match current booking by slot id (upcoming.appointment_id === slots.appointment_slot_id)
      ...(bookedSlotId ? { initialTimeId: bookedSlotId, bookedSlotId } : {}),
    };
  }
  return {
    initialDateId: flow.slotDates[0]?.id ?? null,
  };
}

function getOptionalSubmitLabel(
  flow: Flow,
  rescheduleSubmitLabel?: string,
): { submitLabel?: string } {
  if (flow.slotMode !== 'reschedule' || !rescheduleSubmitLabel) {
    return {};
  }
  return { submitLabel: rescheduleSubmitLabel };
}

function getOptionalDateChangeHandler(flow: Flow): {
  onDateChange?: (dateId: string) => void;
} {
  if (Platform.OS !== 'web') {
    return {};
  }
  return { onDateChange: flow.onSlotDateChange };
}

/**
 * Renders appointment modals from a shared flow hook.
 * Only the active step is mounted so slot + confirmed Modals cannot stack on web.
 */
export function AppointmentFlowDialogs({
  flow,
  rescheduleSubmitLabel,
}: Readonly<AppointmentFlowDialogsProps>) {
  const initialSelection = getSlotInitialSelection(flow);
  const submitLabelProps = getOptionalSubmitLabel(flow, rescheduleSubmitLabel);
  const dateChangeProps = getOptionalDateChangeHandler(flow);

  return (
    <>
      {flow.step === 'slot' ? (
        <AppointmentSlotDialog
          visible
          mode={flow.slotMode}
          dates={flow.slotDates}
          times={flow.times}
          {...initialSelection}
          {...submitLabelProps}
          slotsLoadState={flow.slotsLoadState}
          isSubmitting={flow.isSubmitting}
          onClose={flow.closeSlot}
          onSubmit={flow.submitSlot}
          {...dateChangeProps}
        />
      ) : null}

      {flow.step === 'confirmedSuccess' && flow.confirmedAppointment != null ? (
        <AppointmentConfirmedDialog
          visible
          appointment={flow.confirmedAppointment}
          onOk={flow.dismissConfirmedSuccess}
        />
      ) : null}

      {flow.step === 'cancelConfirm' ? (
        <CancelAppointmentDialog
          visible
          onNo={flow.dismissCancelConfirm}
          onYes={flow.confirmCancel}
        />
      ) : null}

      {flow.step === 'canceledSuccess' ? (
        <AppointmentCanceledDialog
          visible
          onDismiss={flow.dismissCanceledSuccess}
        />
      ) : null}

      <FullScreenLoader visible={flow.isSubmitting} />

      <Toast
        visible={flow.actionError != null}
        message={flow.actionError ?? ''}
        position="top-right"
        onHide={flow.dismissActionError}
      />
    </>
  );
}

export default AppointmentFlowDialogs;
